/**
 * CONSAI Business App - Team Members Management Component
 * Allows business owners to add and manage team members
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Users, Plus, Trash2, Crown, User, Mail, Calendar, Clock, AlertTriangle, Info, Eye, EyeOff, Loader2
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TeamMembers = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [teamData, setTeamData] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });

  // Check if user is owner
  const isOwner = user?.role === 'owner' || !user?.role;

  // Fetch team members
  const fetchTeamMembers = useCallback(async () => {
    if (!isOwner) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/team/members`, { withCredentials: true });
      setTeamData(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        // User is team member, not owner
        setTeamData(null);
      } else {
        toast.error(language === 'hi' ? 'टीम सदस्य लोड करने में विफल' : 'Failed to load team members');
      }
    }
    setLoading(false);
  }, [language, isOwner]);

  useEffect(() => {
    fetchTeamMembers();
  }, [fetchTeamMembers]);

  // Add team member
  const handleAddMember = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error(language === 'hi' ? 'नाम आवश्यक है' : 'Name is required');
      return;
    }
    
    if (!formData.email.trim()) {
      toast.error(language === 'hi' ? 'ईमेल आवश्यक है' : 'Email is required');
      return;
    }
    
    if (formData.password && formData.password.length < 8) {
      toast.error(language === 'hi' ? 'पासवर्ड कम से कम 8 अक्षर होना चाहिए' : 'Password must be at least 8 characters');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/team/members`, {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password || null
      }, { withCredentials: true });
      
      toast.success(language === 'hi' ? 'टीम सदस्य जोड़ा गया' : 'Team member added successfully');
      setShowAddDialog(false);
      setFormData({ name: '', email: '', password: '' });
      fetchTeamMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'hi' ? 'जोड़ने में विफल' : 'Failed to add team member'));
    }
    setSubmitting(false);
  };

  // Remove team member
  const handleRemoveMember = async () => {
    if (!showRemoveDialog) return;
    
    setSubmitting(true);
    try {
      await axios.delete(`${API_URL}/api/team/members/${showRemoveDialog.user_id}`, { withCredentials: true });
      toast.success(language === 'hi' ? 'टीम सदस्य हटाया गया' : 'Team member removed');
      setShowRemoveDialog(null);
      fetchTeamMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'hi' ? 'हटाने में विफल' : 'Failed to remove'));
    }
    setSubmitting(false);
  };

  // If user is team member, show restricted message
  if (!isOwner) {
    return (
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-800 font-manrope">
                {language === 'hi' ? 'मालिक का एक्सेस आवश्यक' : 'Owner Access Required'}
              </h3>
              <p className="text-sm text-amber-700 mt-1">
                {language === 'hi'
                  ? 'केवल व्यवसाय का मालिक टीम सदस्यों को प्रबंधित कर सकता है। सहायता के लिए मालिक से संपर्क करें।'
                  : 'Only the business owner can manage team members. Please contact the owner for assistance.'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin h-8 w-8 border-4 border-[#021945] border-t-transparent rounded-full" />
      </div>
    );
  }

  const currentCount = teamData?.total || 0;
  const maxAllowed = teamData?.max_allowed || 1;
  const remainingSlots = teamData?.remaining_slots || 0;

  return (
    <Card className="bg-white">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 font-manrope">
              <Users className="h-5 w-5 text-[#021945]" />
              {language === 'hi' ? 'टीम सदस्य' : 'Team Members'}
              <Badge variant="secondary" className="ml-2">
                {currentCount}/{maxAllowed}
              </Badge>
            </CardTitle>
            <CardDescription className="mt-1">
              {language === 'hi' 
                ? 'जो आपके व्यवसाय खाते तक पहुंच सकते हैं उन्हें प्रबंधित करें' 
                : 'Manage who can access your business account'}
            </CardDescription>
          </div>
          <Button
            onClick={() => setShowAddDialog(true)}
            disabled={remainingSlots <= 0}
            className="bg-[#021945] hover:bg-[#021945]/90"
            data-testid="add-team-member-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            {language === 'hi' ? 'सदस्य जोड़ें' : 'Add Member'}
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Plan limit info */}
        <div className="bg-slate-50 p-3 rounded-lg text-sm">
          <div className="flex items-center justify-between">
            <span className="text-slate-600">
              {language === 'hi' ? 'प्लान सीमा:' : 'Plan Limit:'}
              <span className="font-medium ml-1">
                {maxAllowed + 1} {language === 'hi' ? 'कुल सदस्य (मालिक सहित)' : 'total members (including owner)'}
              </span>
              <span className="text-slate-500 ml-1">
                ({user?.current_plan === 'premium' || user?.current_plan === 'premium_trial' ? 'Premium' : 'Starter'})
              </span>
            </span>
            {remainingSlots > 0 ? (
              <span className="text-green-600">
                {language === 'hi' ? `${remainingSlots} और जोड़ सकते हैं` : `${remainingSlots} more available`}
              </span>
            ) : (
              <span className="text-amber-600">
                {language === 'hi' ? 'सीमा पूरी' : 'Limit reached'}
              </span>
            )}
          </div>
          {remainingSlots <= 0 && user?.current_plan === 'starter' && (
            <p className="text-amber-600 text-xs mt-2 flex items-center gap-1">
              <Info className="h-3 w-3" />
              {language === 'hi' 
                ? 'और सदस्यों के लिए प्रीमियम में अपग्रेड करें (5 कुल सदस्य)' 
                : 'Upgrade to Premium for more members (5 total members)'}
            </p>
          )}
        </div>

        {/* Owner Card — dark amber accent */}
        {teamData?.owner && (
          <div
            className="border rounded-xl p-4"
            style={{ background: 'rgba(251,191,36,0.06)', borderColor: 'rgba(251,191,36,0.25)' }}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.30)' }}
                >
                  <Crown className="h-5 w-5 text-amber-400" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-white tracking-tight">
                      {teamData.owner.name || (language === 'hi' ? 'आप' : 'You')}
                    </span>
                    <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/30 text-xs">
                      {language === 'hi' ? 'मालिक' : 'Owner'}
                    </Badge>
                  </div>
                  <p className="text-sm text-white/55">
                    {teamData.owner.email || user?.mobile_number || user?.email || ''}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Team Members List */}
        {teamData?.team_members?.length > 0 ? (
          <div className="space-y-3">
            {teamData.team_members.map((member) => (
              <div 
                key={member.user_id} 
                className="border rounded-lg p-4 hover:shadow-sm transition-shadow"
                data-testid={`team-member-${member.user_id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                      <User className="h-5 w-5 text-slate-500" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900 font-manrope">{member.name}</span>
                        <Badge className="bg-blue-100 text-blue-700 text-xs">
                          {language === 'hi' ? 'टीम सदस्य' : 'Team Member'}
                        </Badge>
                        {member.status === 'inactive' && (
                          <Badge variant="destructive" className="text-xs">
                            {language === 'hi' ? 'निष्क्रिय' : 'Inactive'}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 flex items-center gap-1">
                        <Mail className="h-3 w-3" /> {member.email}
                      </p>
                      <div className="flex gap-4 mt-1 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {language === 'hi' ? 'जोड़ा:' : 'Added:'} {new Date(member.added_at).toLocaleDateString('en-IN')}
                        </span>
                        {member.last_login && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {language === 'hi' ? 'अंतिम लॉगिन:' : 'Last login:'} {new Date(member.last_login).toLocaleDateString('en-IN')}
                          </span>
                        )}
                        {!member.last_login && (
                          <span className="text-amber-500">
                            {language === 'hi' ? 'कभी लॉगिन नहीं किया' : 'Never logged in'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowRemoveDialog(member)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    data-testid={`remove-member-${member.user_id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">
            <Users className="h-12 w-12 mx-auto mb-2 text-slate-300" />
            <p className="">
              {language === 'hi' ? 'कोई टीम सदस्य नहीं' : 'No team members yet'}
            </p>
            <p className="text-sm text-slate-400 mt-1">
              {language === 'hi' 
                ? 'टीम सदस्य जोड़ने के लिए "सदस्य जोड़ें" पर क्लिक करें' 
                : 'Click "Add Member" to add team members'}
            </p>
          </div>
        )}
      </CardContent>

      {/* Add Team Member Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-manrope flex items-center gap-2">
              <Users className="h-5 w-5 text-[#021945]" />
              {language === 'hi' ? 'टीम सदस्य जोड़ें' : 'Add Team Member'}
            </DialogTitle>
            <DialogDescription className="">
              {language === 'hi' 
                ? 'यह व्यक्ति आपके व्यवसाय के डेटा तक पहुंच प्राप्त करेगा'
                : 'This person will be able to access your business data'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleAddMember} className="space-y-4">
            <div>
              <Label className="">{language === 'hi' ? 'नाम' : 'Name'} *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={language === 'hi' ? 'टीम सदस्य का नाम' : 'Team member name'}
                className="mt-1"
                data-testid="member-name-input"
              />
            </div>
            
            <div>
              <Label className="">{language === 'hi' ? 'ईमेल' : 'Email Address'} *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@example.com"
                className="mt-1"
                data-testid="member-email-input"
              />
            </div>
            
            <div>
              <Label className="">
                {language === 'hi' ? 'पासवर्ड' : 'Password'}
                <span className="text-slate-400 ml-1">({language === 'hi' ? 'वैकल्पिक' : 'optional'})</span>
              </Label>
              <div className="relative mt-1">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={language === 'hi' ? 'पासवर्ड सेट करें या खाली छोड़ें' : 'Set password or leave blank'}
                  className="pr-10"
                  data-testid="member-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                {language === 'hi' 
                  ? 'पासवर्ड न देने पर, सदस्य को Google Sign-In का उपयोग करना होगा'
                  : 'If no password is set, the member must use Google Sign-In'}
              </p>
            </div>

            <div className="bg-slate-50 p-3 rounded-lg text-xs text-slate-600">
              <p className="font-medium mb-1">{language === 'hi' ? 'टीम सदस्य क्या कर सकते हैं:' : 'Team members can:'}</p>
              <ul className="list-disc list-inside space-y-0.5 text-slate-500">
                <li>{language === 'hi' ? 'ग्राहक, बिक्री और इन्वेंटरी प्रबंधित करें' : 'Manage customers, sales, and inventory'}</li>
                <li>{language === 'hi' ? 'इनवॉइस बनाएं' : 'Create invoices'}</li>
                <li>{language === 'hi' ? 'क्रेडिट/खाताबुक ट्रैक करें' : 'Track credit/khatabook'}</li>
                <li>{language === 'hi' ? 'AI फीचर्स का उपयोग करें' : 'Use AI features'}</li>
              </ul>
              <p className="font-medium mt-2 text-amber-600">
                {language === 'hi' ? 'टीम सदस्य नहीं कर सकते:' : 'Team members cannot:'}
              </p>
              <ul className="list-disc list-inside space-y-0.5 text-amber-500">
                <li>{language === 'hi' ? 'बिलिंग/सदस्यता प्रबंधित करें' : 'Manage billing/subscription'}</li>
                <li>{language === 'hi' ? 'टीम सदस्य जोड़ें/हटाएं' : 'Add/remove team members'}</li>
              </ul>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)} className="">
                {language === 'hi' ? 'रद्द करें' : 'Cancel'}
              </Button>
              <Button
                type="submit"
                className="bg-[#021945] hover:bg-[#021945]/90"
                disabled={submitting}
                data-testid="confirm-add-member-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {language === 'hi' ? 'जोड़ रहा है...' : 'Adding...'}
                  </>
                ) : (
                  language === 'hi' ? 'सदस्य जोड़ें' : 'Add Member'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Remove Team Member Dialog */}
      <Dialog open={!!showRemoveDialog} onOpenChange={(o) => !o && setShowRemoveDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-manrope text-red-600 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              {language === 'hi' ? 'टीम सदस्य हटाएं?' : 'Remove Team Member?'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-slate-600">
              {language === 'hi' 
                ? `क्या आप वाकई ${showRemoveDialog?.name} (${showRemoveDialog?.email}) को हटाना चाहते हैं?`
                : `Are you sure you want to remove ${showRemoveDialog?.name} (${showRemoveDialog?.email})?`}
            </p>
            <p className="text-sm text-amber-600 mt-2">
              {language === 'hi' 
                ? 'वे तुरंत आपके व्यवसाय खाते तक पहुंच खो देंगे।'
                : 'They will immediately lose access to your business account.'}
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowRemoveDialog(null)} className="">
              {language === 'hi' ? 'रद्द करें' : 'Cancel'}
            </Button>
            <Button
              onClick={handleRemoveMember}
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={submitting}
              data-testid="confirm-remove-member-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {language === 'hi' ? 'हटा रहा है...' : 'Removing...'}
                </>
              ) : (
                language === 'hi' ? 'सदस्य हटाएं' : 'Remove Member'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default TeamMembers;
